<template>
  <ContentWrap>
    <el-form ref="queryFormRef" :inline="true" :model="queryParams" class="-mb-15px" label-width="80px">
      <el-form-item label="昵称" prop="nickname">
        <el-input
          v-model="queryParams.nickname"
          class="!w-200px"
          clearable
          placeholder="会员昵称"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-select v-model="queryParams.status" class="!w-160px" clearable placeholder="全部">
          <el-option
            v-for="o in SHAREHOLDER_USER_STATUS"
            :key="o.value"
            :label="o.label"
            :value="o.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="等级" prop="levelId">
        <el-select v-model="queryParams.levelId" class="!w-160px" clearable placeholder="全部">
          <el-option v-for="l in levelOptions" :key="l.id" :label="l.name" :value="l.id" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="warning" @click="filterPending">待审核</el-button>
        <el-button @click="handleQuery"><Icon class="mr-5px" icon="ep:search" />搜索</el-button>
        <el-button @click="resetQuery"><Icon class="mr-5px" icon="ep:refresh" />重置</el-button>
        <el-button
          v-hasPermi="['distribution:shareholder:user:update']"
          plain
          type="primary"
          @click="openCreate"
        >
          <Icon class="mr-5px" icon="ep:plus" />指定股东
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list" :stripe="true" :show-overflow-tooltip="true">
      <el-table-column align="center" label="编号" prop="id" width="70" />
      <el-table-column align="center" label="头像" width="70">
        <template #default="{ row }">
          <el-avatar :src="row.avatar" />
        </template>
      </el-table-column>
      <el-table-column align="center" label="会员" min-width="140">
        <template #default="{ row }">
          <div>{{ row.nickname || '-' }}</div>
          <div class="text-xs text-gray-400">ID:{{ row.userId }} {{ row.mobile || '' }}</div>
        </template>
      </el-table-column>
      <el-table-column align="center" label="等级" prop="levelName" min-width="100" />
      <el-table-column align="center" label="状态" width="90">
        <template #default="{ row }">
          <el-tag :type="tagTypeOf(SHAREHOLDER_USER_STATUS, row.status)" size="small">
            {{ labelOf(SHAREHOLDER_USER_STATUS, row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column align="center" label="累计分红" min-width="90">
        <template #default="{ row }">￥{{ fenToYuan(row.totalBonus || 0) }}</template>
      </el-table-column>
      <el-table-column align="center" label="已提现" min-width="90">
        <template #default="{ row }">￥{{ fenToYuan(row.withdrawBonus || 0) }}</template>
      </el-table-column>
      <el-table-column align="center" label="冻结" min-width="90">
        <template #default="{ row }">￥{{ fenToYuan(row.freezeBonus || 0) }}</template>
      </el-table-column>
      <el-table-column
        align="center"
        label="成为时间"
        prop="becomeTime"
        :formatter="dateFormatter"
        width="170"
      />
      <el-table-column align="center" label="操作" fixed="right" width="220">
        <template #default="{ row }">
          <template v-if="row.status === 0">
            <el-button
              v-hasPermi="['distribution:shareholder:user:audit']"
              link
              type="primary"
              @click="openAudit(row, true)"
            >
              通过
            </el-button>
            <el-button
              v-hasPermi="['distribution:shareholder:user:audit']"
              link
              type="danger"
              @click="openAudit(row, false)"
            >
              拒绝
            </el-button>
          </template>
          <template v-if="row.status === 1">
            <el-button
              v-hasPermi="['distribution:shareholder:user:update']"
              link
              type="primary"
              @click="openLevel(row)"
            >
              改等级
            </el-button>
            <el-button
              v-hasPermi="['distribution:shareholder:user:update']"
              link
              type="warning"
              @click="changeStatus(row, 3)"
            >
              禁用
            </el-button>
            <el-button
              v-hasPermi="['distribution:shareholder:user:update']"
              link
              type="danger"
              @click="changeStatus(row, 4)"
            >
              黑名单
            </el-button>
            <el-button
              v-hasPermi="['distribution:shareholder:user:update']"
              link
              type="success"
              @click="handleTryUpgrade(row)"
            >
              升级检测
            </el-button>
          </template>
          <template v-if="row.status === 3 || row.status === 4">
            <el-button
              v-hasPermi="['distribution:shareholder:user:update']"
              link
              type="primary"
              @click="changeStatus(row, 1)"
            >
              恢复正常
            </el-button>
          </template>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <ShareholderUserCreateForm ref="createRef" @success="getList" />
  <ShareholderUserAuditForm ref="auditRef" @success="getList" />
  <ShareholderUserLevelForm ref="levelRef" @success="getList" />
</template>

<script lang="ts" setup>
import { dateFormatter } from '@/utils/formatTime'
import { fenToYuan } from '@/utils'
import * as UserApi from '@/api/mall/trade/shareholder/user'
import * as LevelApi from '@/api/mall/trade/shareholder/level'
import {
  SHAREHOLDER_USER_STATUS,
  labelOf,
  tagTypeOf
} from '../constants'
import ShareholderUserCreateForm from './ShareholderUserCreateForm.vue'
import ShareholderUserAuditForm from './ShareholderUserAuditForm.vue'
import ShareholderUserLevelForm from './ShareholderUserLevelForm.vue'

defineOptions({ name: 'TradeShareholderUser' })

const message = useMessage()
const loading = ref(false)
const list = ref<UserApi.ShareholderUserVO[]>([])
const total = ref(0)
const levelOptions = ref<LevelApi.ShareholderLevelSimpleVO[]>([])
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  nickname: undefined as string | undefined,
  status: undefined as number | undefined,
  levelId: undefined as number | undefined
})
const queryFormRef = ref()
const createRef = ref()
const auditRef = ref()
const levelRef = ref()

const getList = async () => {
  loading.value = true
  try {
    const data = await UserApi.getShareholderUserPage(queryParams)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryFormRef.value?.resetFields()
  handleQuery()
}

const filterPending = () => {
  queryParams.status = 0
  handleQuery()
}

const openCreate = () => createRef.value?.open()
const openAudit = (row: UserApi.ShareholderUserVO, approved: boolean) =>
  auditRef.value?.open(row, approved)
const openLevel = (row: UserApi.ShareholderUserVO) => levelRef.value?.open(row)

const changeStatus = async (row: UserApi.ShareholderUserVO, status: number) => {
  const tip =
    status === 1 ? '确认恢复为正常股东？' : status === 3 ? '确认禁用该股东？' : '确认加入黑名单？'
  try {
    await message.confirm(tip)
    await UserApi.updateShareholderUserStatus({ id: row.id!, status })
    message.success('操作成功')
    await getList()
  } catch {}
}

const handleTryUpgrade = async (row: UserApi.ShareholderUserVO) => {
  try {
    await message.confirm('将按当前升级依据检测，最多升一级，是否继续？')
    const ok = await UserApi.tryUpgradeShareholderUser(row.userId!)
    message.success(ok ? '已升级一级' : '未达升级条件')
    await getList()
  } catch {}
}

onMounted(async () => {
  levelOptions.value = (await LevelApi.getShareholderLevelSimpleList()) || []
  await getList()
})
</script>
